import React, { Component } from 'react';
import { Button } from 'primereact/button';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import { STATUS, STATUS_LABEL } from './mockFloorPlanData';

const TAG_ICON = {
    [STATUS.QR]: 'pi-qrcode',
    [STATUS.PROGRESS]: 'pi-spin pi-spinner',
    [STATUS.BOOKING]: 'pi-calendar',
    [STATUS.RESERVED]: 'pi-lock',
    [STATUS.AVAILABLE]: 'pi-check-circle',
};

class ActionPanel extends Component {

    // Closing an order is hard to reverse from here, so confirm first —
    // same react-confirm-alert pattern POSTopbar.js's logout prompt uses.
    confirmCloseOrder = (table) => {
        const { onCloseOrder } = this.props;
        confirmAlert({
            title: 'Close this order?',
            message: `Table ${table.id} will be closed. This can't be undone from here.`,
            buttons: [
                { label: 'Close Order', onClick: () => onCloseOrder && onCloseOrder(table) },
                { label: 'Cancel' },
            ],
        });
    };

    renderEmpty() {
        return (
            <div className="captain-action-empty">
                <i className="pi pi-clone" style={{ fontSize: '42px', color: '#c8c8c8' }} />
                <h3>Select a Table</h3>
                <p>Choose a table from the floor plan to view details and perform actions.</p>
            </div>
        );
    }

    renderNoOrder(table) {
        const { onClose } = this.props;
        return (
            <div className="captain-detail captain-detail--plain">
                <div className="captain-detail-header captain-detail-header--plain">
                    <span>Table {table.id}</span>
                    <button type="button" className="captain-detail-close" onClick={onClose} aria-label="Close">
                        <i className="pi pi-times" />
                    </button>
                </div>
                <div className="captain-detail-body">
                    <div className="captain-detail-status-label">{STATUS_LABEL[STATUS.AVAILABLE]}</div>
                    <div className="captain-panel-actions">
                        {/* <Button label="Seat Guests" icon="pi pi-users" className="p-button-success p-mb-2" /> */}
                        {/* <Button label="New Order" icon="pi pi-shopping-cart" className="p-button-primary p-mb-2" onClick={() => } /> */}
                        {/* <Button label="Reserve Table" icon="pi pi-calendar" className="p-button-warning" /> */}
                    </div>
                </div>
            </div>
        );
    }

    // Item-level KOT rows for this table's order — same GetresActionAllList
    // data and Restaurant_Action_Update write Waiter.js uses, just scoped to
    // the one order behind this table instead of every open order. Exactly
    // Waiter.js's own rule: a 'preparing' item (still with the kitchen) gets
    // no checkbox at all, just a "Not Ready" label — only a 'prepared' item
    // can be checked off and marked served.
    renderKotItems() {
        const { kotItems = [], onToggleKotItem, onMarkKotServed } = this.props;

        if (kotItems.length === 0) {
            return <div className="captain-kot-empty">No KOT items found for this order yet.</div>;
        }

        const hasSubmittable = kotItems.some(it => it.selected && it.status === 'prepared');

        return (
            <>
                <div className="captain-kot-items">
                    {kotItems.map(item => (
                        <div key={item.id} className={`captain-kot-item captain-kot-item--${item.status}`}>
                            <span className="captain-kot-item-qty">&times;{item.qty}</span>
                            <span className="captain-kot-item-name">{item.name}</span>

                            {item.status === 'preparing' ? (
                                <span className="captain-kot-item-not-ready">Not Ready</span>
                            ) : (
                                <label className="captain-kot-item-check">
                                    <input
                                        type="checkbox"
                                        checked={item.selected}
                                        disabled={item.status === 'served'}
                                        onChange={(e) => onToggleKotItem(item.id, e.target.checked)}
                                    />
                                    {item.status === 'served' ? 'Served' : 'Mark Served'}
                                </label>
                            )}
                        </div>
                    ))}
                </div>

                <Button
                    label="Mark Selected Served"
                    icon="pi pi-check"
                    className="p-button-success captain-kot-mark-ready"
                    disabled={!hasSubmittable}
                    onClick={() => onMarkKotServed(kotItems)}
                />
            </>
        );
    }

    // Order summary is only what GetrestableOrderNum actually returns
    // (ordereNumber, duration, amount, requestId) — no fabricated stepper/
    // history here. See API_MAPPING.md's "Order Status stepper" and "Table
    // History" sections for what's still missing to build those for real.
    renderRunningOrder(table) {
        const { onClose, onViewOrder } = this.props;
        const { runningOrder } = table;

        return (
            <div className="captain-detail">

                <div className="captain-detail-header">
                    <span>Table {table.id}</span>
                    <button type="button" className="captain-detail-close" onClick={onClose} aria-label="Close">
                        <i className="pi pi-times" />
                    </button>
                </div>

                <div className="captain-detail-body">

                    <div className="captain-detail-tags">
                        <span className={`captain-tag captain-tag--${table.status}`}>
                            <i className={`pi ${TAG_ICON[table.status]}`} /> {STATUS_LABEL[table.status]}
                        </span>
                    </div>

                    <div className="captain-detail-section">
                        <div className="captain-section-header">
                            <span>Running Order</span>
                        </div>

                        <div className="captain-order-items">
                            {runningOrder.ordereNumber && (
                                <div className="captain-order-item">
                                    <span className="captain-order-item-name">Order No</span>
                                    <span className="captain-order-item-price">{runningOrder.ordereNumber}</span>
                                </div>
                            )}
                            {runningOrder.duration && (
                                <div className="captain-order-item">
                                    <span className="captain-order-item-name">Duration</span>
                                    <span className="captain-order-item-price">{runningOrder.duration}</span>
                                </div>
                            )}
                        </div>

                        {runningOrder.amount != null && runningOrder.amount !== '' && (
                            <div className="captain-order-total">
                                <span>Amount</span>
                                <span>₹{runningOrder.amount}</span>
                            </div>
                        )}
                    </div>

                    <div className="captain-detail-section">
                        <div className="captain-section-header">
                            <span>Kitchen Items</span>
                        </div>
                        {this.renderKotItems()}
                    </div>

                    <div className="captain-detail-section">
                        <div className="captain-section-header">
                            <span>Quick Actions</span>
                        </div>

                        <div className="captain-quick-actions">
                            <button
                                type="button"
                                className="captain-quick-action captain-quick-action--view"
                                onClick={() => onViewOrder && onViewOrder(runningOrder.requestId)}
                            >
                                <i className="pi pi-external-link" /> <span>View Order</span> <i className="pi pi-angle-right" />
                            </button>
                            <button
                                type="button"
                                className="captain-quick-action captain-quick-action--close"
                                onClick={() => this.confirmCloseOrder(table)}
                            >
                                <i className="pi pi-times-circle" /> <span>Close Order</span> <i className="pi pi-angle-right" />
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        );
    }

    render() {
        const { table } = this.props;

        if (!table) return this.renderEmpty();
        if (!table.runningOrder) return this.renderNoOrder(table);
        return this.renderRunningOrder(table);
    }
}

export default ActionPanel;
